import './AuthModal.css'
import { useEffect, useState } from 'react'
import { useUser } from '../context/UserContext'
import { friendly } from '../services/userAuth'

// Sign in / sign up / forgot password / choose new password, all in one popup.
// Keep this component mounted once for the whole site (where the old sign-in popup was),
// so the link in the reset email (/?reset=TOKEN) can open it.

const NAME_RE = /^\p{L}[\p{L} .'-]*$/u
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const EMPTY = { name: '', email: '', phone: '', password: '', confirm: '' }
const TITLES = {
  login: 'Welcome back',
  signup: 'Create your account',
  forgot: 'Forgot your password?',
  reset: 'Choose a new password',
}

function validate(mode, f) {
  const e = {}
  if (mode === 'signup') {
    const n = f.name.trim()
    if (!n) e.name = 'Enter your full name.'
    else if (n.length < 2 || n.length > 60 || !NAME_RE.test(n)) e.name = 'Enter a valid name (letters only).'
    if (!f.phone) e.phone = 'Enter your phone number.'
    else if (!/^[6-9]\d{9}$/.test(f.phone)) e.phone = 'Enter a valid 10-digit mobile number.'
  }
  if (mode !== 'reset') {
    const em = f.email.trim()
    if (!em) e.email = 'Enter your email address.'
    else if (!EMAIL_RE.test(em)) e.email = 'Enter a valid email, like name@example.com.'
  }
  if (mode === 'login' && !f.password) e.password = 'Enter your password.'
  if (mode === 'signup' || mode === 'reset') {
    if (f.password.length < 8) e.password = 'Password must be at least 8 characters.'
    else if (f.password.length > 72) e.password = 'Password is too long (72 characters at most).'
    if (!f.confirm) e.confirm = 'Confirm your password.'
    else if (f.confirm !== f.password) e.confirm = 'Passwords do not match.'
  }
  return e
}

export default function AuthModal() {
  const { authOpen, authMode, openAuth, closeAuth, signUp, logIn, requestReset, resetPassword } = useUser()
  const [mode, setMode] = useState('login')
  const [f, setF] = useState(EMPTY)
  const [resetToken, setResetToken] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [busy, setBusy] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [done, setDone] = useState('') // success message shown after the account is created

  // arriving from the reset email: /?reset=TOKEN opens the "new password" form
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('reset')
    if (!t) return
    setResetToken(t)
    setMode('reset')
    openAuth('reset')
    const u = new URL(window.location.href)
    u.searchParams.delete('reset') // keep the secret token out of the address bar
    window.history.replaceState({}, '', u.pathname + u.search + u.hash)
  }, [openAuth])

  // open on the form the caller asked for (log in, sign up or reset)
  useEffect(() => { if (authOpen) setMode(authMode) }, [authOpen, authMode])

  // Escape closes whichever popup is showing
  useEffect(() => {
    if (!authOpen && !done) return
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      if (done) setDone('')
      else close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOpen, done])

  // success popup closes itself after a few seconds
  useEffect(() => {
    if (!done) return
    const t = setTimeout(() => setDone(''), 5000)
    return () => clearTimeout(t)
  }, [done])

  // put the cursor in the first field
  useEffect(() => {
    if (!authOpen) return
    const first = { login: 'email', signup: 'name', forgot: 'email', reset: 'password' }[mode]
    const t = setTimeout(() => document.getElementById(`au-${first}`)?.focus(), 60)
    return () => clearTimeout(t)
  }, [authOpen, mode])

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }))
  const errors = validate(mode, f)
  const show = (k) => submitted && errors[k]

  const go = (next, message = '') => {
    setMode(next); setError(''); setInfo(message); setSubmitted(false)
    setF((x) => ({ ...EMPTY, email: x.email })) // keep the email, clear the rest
    setShowPw(false)
  }
  const reset = () => { setMode('login'); setF(EMPTY); setError(''); setInfo(''); setSubmitted(false); setShowPw(false); setResetToken('') }
  const close = () => { reset(); closeAuth() }

  const submit = async (e) => {
    e.preventDefault()
    setSubmitted(true); setError(''); setInfo('')
    const keys = Object.keys(errors)
    if (keys.length) { document.getElementById(`au-${keys[0]}`)?.focus(); return }
    setBusy(true)
    try {
      if (mode === 'login') { await logIn(f.email.trim(), f.password); reset() }
      else if (mode === 'signup') {
        const firstName = f.name.trim().split(' ')[0]
        await signUp({ name: f.name.trim(), email: f.email.trim(), phone: f.phone, password: f.password, confirm_password: f.confirm })
        close()
        setDone(`Welcome, ${firstName}! Your account is ready and you are signed in.`)
      } else if (mode === 'forgot') setInfo(await requestReset(f.email.trim()))
      else go('login', await resetPassword(resetToken, f.password, f.confirm))
    } catch (err) {
      setError(friendly(err))
    } finally {
      setBusy(false)
    }
  }

  if (!authOpen && !done) return null

  const input = (k, label, props = {}) => (
    <label>{label}
      <input
        id={`au-${k}`} value={f[k]} onChange={(e) => set(k, props.digits ? e.target.value.replace(/\D/g, '') : e.target.value)}
        aria-invalid={!!show(k)} aria-describedby={show(k) ? `au-${k}-err` : undefined} {...props.attrs}
      />
      {show(k) && <small className="au__err" id={`au-${k}-err`} role="alert">{errors[k]}</small>}
    </label>
  )
  const pw = (k, label, auto) => input(k, label, { attrs: { type: showPw ? 'text' : 'password', autoComplete: auto } })
  const link = (text, next) => <button type="button" className="au__link" onClick={() => go(next)}>{text}</button>

  return (
    <>
      {authOpen && (
        <div className="au" role="dialog" aria-modal="true" aria-label={TITLES[mode]}>
          <div className="au__scrim" onClick={close} />
          <div className="au__box">
            <button className="au__x btn btn--sm" type="button" onClick={close} aria-label="Close">Close</button>
            <form onSubmit={submit} noValidate>
              <h2>{TITLES[mode]}</h2>
              {mode === 'forgot' && <p className="au__sub">Enter your email and we will send you a link to choose a new password.</p>}
              {mode === 'reset' && <p className="au__sub">Use at least 8 characters.</p>}

              {mode === 'signup' && input('name', 'Full name', { attrs: { autoComplete: 'name' } })}
              {mode !== 'reset' && input('email', 'Email', { attrs: { type: 'email', autoComplete: 'email', placeholder: 'name@example.com' } })}
              {mode === 'signup' && input('phone', 'Phone number', {
                digits: true, attrs: { type: 'tel', inputMode: 'numeric', autoComplete: 'tel-national', maxLength: 10, placeholder: '10-digit mobile number' },
              })}
              {mode === 'login' && pw('password', 'Password', 'current-password')}
              {(mode === 'signup' || mode === 'reset') && pw('password', mode === 'reset' ? 'New password' : 'Password', 'new-password')}
              {(mode === 'signup' || mode === 'reset') && pw('confirm', 'Confirm password', 'new-password')}

              {mode !== 'forgot' && (
                <label className="au__show">
                  <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} /> Show password
                </label>
              )}

              {error && <p className="au__error" role="alert">{error}</p>}
              {info && <p className="au__info" role="status">{info}</p>}

              <button className="btn btn--cobalt" disabled={busy}>
                {busy ? 'Please wait…' : { login: 'Log in', signup: 'Create account', forgot: 'Send reset link', reset: 'Save new password' }[mode]}
              </button>

              <div className="au__links">
                {mode === 'login' && <>{link('Forgot password?', 'forgot')}<span>New here? {link('Create an account', 'signup')}</span></>}
                {mode === 'signup' && <span>Already have an account? {link('Log in', 'login')}</span>}
                {(mode === 'forgot' || mode === 'reset') && link('Back to log in', 'login')}
              </div>
            </form>
          </div>
        </div>
      )}

      {done && (
        <div className="au" role="alertdialog" aria-modal="true" aria-label="Account created">
          <div className="au__scrim" onClick={() => setDone('')} />
          <div className="au__box au__success">
            <div className="au__tick" aria-hidden="true">✓</div>
            <h2>Account created</h2>
            <p role="status">{done}</p>
            <button className="btn btn--cobalt" type="button" autoFocus onClick={() => setDone('')}>Start shopping</button>
          </div>
        </div>
      )}
    </>
  )
}